import { c as _c } from "react/compiler-runtime";
import { UserAvatarWithMenu } from "@components/user-more-info-menu/user-avatar-with-menu";
import { RemoveXgSeatCheck } from "@components/table/members/remove-xg-seat/remove-xg-seat-check";
import { RemoveXgSeatConfirmStep } from "@components/table/members/remove-xg-seat/remove-xg-seat-confirm-step";
import { RemoveXgSeatGroupsStep } from "@components/table/members/remove-xg-seat/remove-xg-seat-groups-step";
import { RemoveXgSeatManagedProjectsStep } from "@components/table/members/remove-xg-seat/remove-xg-seat-managed-projects-step";
import { RemoveXgSeatProjectsStep } from "@components/table/members/remove-xg-seat/remove-xg-seat-projects-step";
import { RemoveXgSeatTeamsStep } from "@components/table/members/remove-xg-seat/remove-xg-seat-teams-step";
import { getStepsAhead, isWorkspaceAdmin } from "@components/table/members/remove-xg-seat/remove-xg-seat-utils";
import { RemoveXgSeatWorkspaceRoleStep } from "@components/table/members/remove-xg-seat/remove-xg-seat-workspace-role-step";
import { useRemoveXgSeat } from "@components/table/members/remove-xg-seat/use-remove-xg-seat";
import { Dialog, PageProgressIndicator, Text } from "@flat-ui";
import { Box, Stack } from "@mui-wrapper";
import { FaroCoreApiUtils } from "@stellar/core-api-client";
import { companyMemberSelector } from "@store/members/members-selector";
import { useAppSelector } from "@store/store-helper";
import { getPrettyName } from "@utils/user-utils";
import { useState } from "react";

/** The label of the action, so that whatever opens the dialog repeats what was clicked */
export const REMOVE_XG_SEAT_TITLE = "Remove Sphere XG seat";
/** Shows the content of a step */
function StepContent(t0) {
  const $ = _c(18);
  const {
    step,
    member,
    newWorkspaceRole,
    onNewWorkspaceRoleChange,
    onBusyChange,
    onResolvedChange
  } = t0;
  let t1;
  if ($[0] !== member || $[1] !== onBusyChange || $[2] !== onResolvedChange) {
    t1 = {
      member,
      onBusyChange,
      onResolvedChange
    };
    $[0] = member;
    $[1] = onBusyChange;
    $[2] = onResolvedChange;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const listStepProps = t1;
  switch (step) {
    case "teams":
      {
        let t2;
        if ($[4] !== listStepProps) {
          t2 = <RemoveXgSeatTeamsStep {...listStepProps} />;
          $[4] = listStepProps;
          $[5] = t2;
        } else {
          t2 = $[5];
        }
        return t2;
      }
    case "groups":
      {
        let t2;
        if ($[6] !== listStepProps) {
          t2 = <RemoveXgSeatGroupsStep {...listStepProps} />;
          $[6] = listStepProps;
          $[7] = t2;
        } else {
          t2 = $[7];
        }
        return t2;
      }
    case "managedProjects":
      {
        let t2;
        if ($[8] !== listStepProps) {
          t2 = <RemoveXgSeatManagedProjectsStep {...listStepProps} />;
          $[8] = listStepProps;
          $[9] = t2;
        } else {
          t2 = $[9];
        }
        return t2;
      }
    case "projects":
      {
        let t2;
        if ($[10] !== listStepProps) {
          t2 = <RemoveXgSeatProjectsStep {...listStepProps} />;
          $[10] = listStepProps;
          $[11] = t2;
        } else {
          t2 = $[11];
        }
        return t2;
      }
    case "workspaceRole":
      {
        let t2;
        if ($[12] !== member || $[13] !== newWorkspaceRole || $[14] !== onNewWorkspaceRoleChange) {
          t2 = <RemoveXgSeatWorkspaceRoleStep member={member} newRole={newWorkspaceRole} onNewRoleChange={onNewWorkspaceRoleChange} />;
          $[12] = member;
          $[13] = newWorkspaceRole;
          $[14] = onNewWorkspaceRoleChange;
          $[15] = t2;
        } else {
          t2 = $[15];
        }
        return t2;
      }
    case "confirm":
      {
        let t2;
        if ($[16] !== member) {
          t2 = <RemoveXgSeatConfirmStep member={member} />;
          $[16] = member;
          $[17] = t2;
        } else {
          t2 = $[17];
        }
        return t2;
      }
  }
}

/** Shows who loses the seat */
function Header(t0) {
  const $ = _c(9);
  const {
    member
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      gap: 1,
      alignItems: "center"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  let t3;
  if ($[1] !== member) {
    t2 = <UserAvatarWithMenu member={member} />;
    t3 = getPrettyName(member);
    $[1] = member;
    $[2] = t2;
    $[3] = t3;
  } else {
    t2 = $[2];
    t3 = $[3];
  }
  let t4;
  if ($[4] !== t3) {
    t4 = <strong><var>{t3}</var></strong>;
    $[4] = t3;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== t2 || $[7] !== t4) {
    t5 = <Stack direction="row" sx={t1}>{t2}{t4}</Stack>;
    $[6] = t2;
    $[7] = t4;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  return t5;
}
/**
 * Dialog that takes the Sphere XG seat of a member away. A member without a seat cannot hold any
 * editor role, so the dialog walks the user through only the places where RemoveXgSeatCheck found
 * the member to hold one, to take it away there:
 *
 * 1. The teams with a workspace role that needs a seat
 * 2. The groups in which the member is a group manager or a project manager
 * 3. The projects the member manages
 * 4. The projects in which the member is an admin or an editor
 * 5. The new workspace role, when the member is an admin, or else the confirmation
 *
 * The user can only go on once nothing is listed in a step anymore. The steps ahead are looked up
 * again then, since a change in one step may resolve another, e.g. leaving a team that made the
 * member a group manager. The steps the user went through stay, to go back to. Only the last step
 * takes the seat away.
 */
export function RemoveXgSeatDialog({
  member,
  steps: initialSteps,
  onClose
}) {
  const storedMember = useAppSelector(companyMemberSelector(FaroCoreApiUtils.Member.getMemberId(member))) ?? member;
  const {
    removeXgSeat
  } = useRemoveXgSeat();
  const [steps, setSteps] = useState(initialSteps);
  const [stepIndex, setStepIndex] = useState(0);
  // Whether the steps ahead are being looked up again, before going on to the next one
  const [isCheckingStepsAhead, setIsCheckingStepsAhead] = useState(false);
  const [newWorkspaceRole, setNewWorkspaceRole] = useState("COMPANY_VIEWER");
  const [isStepBusy, setIsStepBusy] = useState(false);
  // The step that reported to be done, kept by key so that a step is never taken as done because
  // the one before it was
  const [resolvedStep, setResolvedStep] = useState(null);
  const [isRemoving, setIsRemoving] = useState(false);
  const step = steps[stepIndex];
  const isFirstStep = stepIndex === 0;
  const isLastStep = stepIndex === steps.length - 1;
  // The last step only asks for a choice, which always has a value
  const canGoOn = isLastStep || resolvedStep === step.key;
  return <Dialog open title={REMOVE_XG_SEAT_TITLE} size="l" confirmText={isLastStep ? "Remove seat" : "Next"} onConfirm={isLastStep ? removeSeat : goToNextStep} isConfirmDisabled={isStepBusy || !canGoOn || isCheckingStepsAhead} confirmTooltip={canGoOn || isCheckingStepsAhead ? undefined : "Resolve every role listed in this step first"} showSpinner={isRemoving || isCheckingStepsAhead} cancelText={isFirstStep ? "Cancel" : "Back"} onCancel={isFirstStep ? closeDialog : goToPreviousStep} onClose={closeDialog} dataTestId="remove-xg-seat-dialog">
      <Stack sx={{
      gap: 2
    }}>
        <Header member={storedMember} />

        <Text variant="heading16">{step.title}</Text>

        <StepContent key={step.key} step={step.key} member={storedMember} newWorkspaceRole={newWorkspaceRole} onNewWorkspaceRoleChange={setNewWorkspaceRole} onBusyChange={setIsStepBusy} onResolvedChange={isResolved => setResolvedStep(isResolved ? step.key : null)} />

        {steps.length > 1 && <Box role="group"
      // The dots only show the progress, so it is spelled out for assistive technologies
      aria-label={`Step ${stepIndex + 1} of ${steps.length}`} sx={{
        display: "flex",
        justifyContent: "center"
      }}>
            <PageProgressIndicator activePageIndex={stepIndex} totalNumberOfPages={steps.length} />
          </Box>}

        {isCheckingStepsAhead && <RemoveXgSeatCheck member={storedMember} onStepsFound={goOnWithSteps} />}
      </Stack>
    </Dialog>;

  /**
   * Closes the dialog, unless a change is in progress, so that it cannot go on unseen once the
   * dialog is gone
   */
  function closeDialog() {
    if (!isRemoving && !isStepBusy && !isCheckingStepsAhead) {
      onClose();
    }
  }

  /** Looks up the steps ahead again once the current step is done, to go on to the next one */
  function goToNextStep() {
    if (!isStepBusy && canGoOn && !isCheckingStepsAhead) {
      setIsCheckingStepsAhead(true);
    }
  }

  /** Shows the next step, out of the steps ahead that are relevant now */
  function goOnWithSteps(foundSteps) {
    setSteps(getStepsAhead(steps, stepIndex, foundSteps));
    setStepIndex(index => index + 1);
    setIsCheckingStepsAhead(false);
  }

  /** Shows the previous step, once the actions of the current one are done */
  function goToPreviousStep() {
    if (!isStepBusy && !isRemoving && !isCheckingStepsAhead) {
      setStepIndex(index_0 => index_0 - 1);
    }
  }

  /**
   * Gives an admin the chosen workspace role and takes the seat away. The dialog stays open when
   * that failed, so that the user can try again.
   */
  async function removeSeat() {
    setIsRemoving(true);
    const isRemoved = await removeXgSeat(storedMember, isWorkspaceAdmin(storedMember) ? newWorkspaceRole : undefined);
    if (isRemoved) {
      onClose();
    } else {
      setIsRemoving(false);
    }
  }
}